import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { useCallback } from 'react';

import { apiLogger } from '~/app';
import { useClearAuthDataAndTraining } from '~/auth';
import {
  LoginRequest,
  LoginResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  RegistrationRequest,
  RelinkRequest,
} from '~/server/src/dtos';

import { postFn, queryClient, RequestError, retry } from './api';
import { useFirebaseAnalytics, useFirebaseAuth } from './useFirebase';

export const auth = createQueryKeys('auth', {
  detail: null,
});

export const postLogin = `/login`;
export const postRegister = `/register`;
export const postRelink = `/relink`;
export const postPasswordReset = `/password-reset`;
export const postRefreshSession = `/auth/refresh`;

export const useRegister = (
  options?: Parameters<typeof useMutation<LoginResponse, RequestError, RegistrationRequest>>[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<LoginResponse, RequestError, RegistrationRequest>({
    mutationFn: (data) => postFn(postRegister, data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ predicate: () => true });
      options?.onSuccess?.(...response);
      analytics?.logEvent('sign_up', { method: 'email' });
    },
  });
};

export const useLogin = (
  options?: Parameters<typeof useMutation<LoginResponse, RequestError, LoginRequest>>[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<LoginResponse, RequestError, LoginRequest>({
    mutationFn: (data) => postFn(postLogin, data),
    ...options,
    retry:
      options?.retry ??
      ((failureCount, error) => {
        // don't retry not found response for login which is indicator for a necessary registration
        if (error.status === 404) {
          return false;
        }
        return retry(failureCount, error);
      }),
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ predicate: () => true });
      options?.onSuccess?.(...response);
      analytics?.logEvent('login', { method: 'email' });
    },
  });
};

/** Silent session refresh using HttpOnly refresh cookie. No Turnstile, no Firebase token in body. */
export const useRefreshSession = (
  options?: Parameters<typeof useMutation<LoginResponse, RequestError, void>>[0],
) => {
  return useMutation<LoginResponse, RequestError, void>({
    mutationFn: () => postFn(postRefreshSession),
    ...options,
  });
};

export const useRelink = (
  options?: Parameters<typeof useMutation<LoginResponse, RequestError, RelinkRequest>>[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<LoginResponse, RequestError, RelinkRequest>({
    mutationFn: (data) => postFn(postRelink, data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ predicate: () => true });
      options?.onSuccess?.(...response);
      analytics?.logEvent('login', { method: 'relink' });
      analytics?.logEvent('relink_success', { method: 'email' });
    },
    onError: (error, ...args) => {
      const status = (error as RequestError).status;
      analytics?.logEvent('relink_failed', { method: 'email', status: String(status) });
      options?.onError?.(error, ...args);
    },
  });
};

export const usePasswordReset = (
  options?: Parameters<
    typeof useMutation<PasswordResetResponse, RequestError, PasswordResetRequest>
  >[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<PasswordResetResponse, RequestError, PasswordResetRequest>({
    mutationFn: (data) => postFn(postPasswordReset, data),
    ...options,
    onSuccess: (...response) => {
      options?.onSuccess?.(...response);
      analytics?.logEvent('password_reset_requested', { method: 'email' });
    },
  });
};

export const useLogout = (onLogout: () => void) => {
  const auth = useFirebaseAuth();
  const analytics = useFirebaseAnalytics();
  const clearAuthDataAndTraining = useClearAuthDataAndTraining();

  return useCallback(async () => {
    try {
      if (auth) {
        await signOut(auth);
      }

      // Intentional logout - clear all data including training data
      clearAuthDataAndTraining();

      onLogout();
      analytics?.logEvent('logout', { method: 'email' });
    } catch (e) {
      apiLogger.warn({ err: e }, 'logout error');
    }
  }, [analytics, auth, onLogout, clearAuthDataAndTraining]);
};
